import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api/client';
import type { AdminCategory, AdminPlanEdit, BillingType } from '../../types';

const EMPTY: AdminPlanEdit = {
  id: '',
  slug: '',
  name: '',
  description: '',
  billingType: 'monthly',
  priceCents: 0,
  providerPriceId: null,
  isActive: true,
  sortOrder: 0,
  categoryIds: [],
};

export function PlanEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [plan, setPlan] = useState<AdminPlanEdit>(EMPTY);
  const [allCategories, setAllCategories] = useState<AdminCategory[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    void (async () => {
      const cats = await api<AdminCategory[]>('/api/admin/categories');
      setAllCategories(cats);
      if (!isNew) {
        const p = await api<AdminPlanEdit>(`/api/admin/plans/${id}`);
        setPlan(p);
      }
    })().catch((err) => setError(err instanceof Error ? err.message : 'Load failed'));
  }, [id, isNew]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body = JSON.stringify({
        slug: plan.slug,
        name: plan.name,
        description: plan.description,
        billingType: plan.billingType,
        priceCents: plan.priceCents,
        providerPriceId: plan.providerPriceId ?? null,
        isActive: plan.isActive,
        sortOrder: plan.sortOrder,
        categoryIds: plan.categoryIds,
      });
      if (isNew) await api('/api/admin/plans', { method: 'POST', body });
      else await api(`/api/admin/plans/${id}`, { method: 'PUT', body });
      navigate('/admin/plans');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  function toggleCategory(catId: string) {
    setPlan((p) => ({
      ...p,
      categoryIds: p.categoryIds.includes(catId)
        ? p.categoryIds.filter((x) => x !== catId)
        : [...p.categoryIds, catId],
    }));
  }

  return (
    <section className="admin-page">
      <header className="admin-page-header">
        <h1>{isNew ? 'New plan' : `Edit plan: ${plan.name || ''}`}</h1>
      </header>
      {error && <div className="form-error" role="alert">{error}</div>}
      <form onSubmit={(e) => void onSubmit(e)} className="admin-form">
        <div className="form-row-pair">
          <div className="form-group">
            <label htmlFor="slug">Slug</label>
            <input id="slug" type="text" required value={plan.slug} onChange={(e) => setPlan({ ...plan, slug: e.target.value })} disabled={submitting} />
          </div>
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input id="name" type="text" required value={plan.name} onChange={(e) => setPlan({ ...plan, name: e.target.value })} disabled={submitting} />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="description">Description</label>
          <input id="description" type="text" value={plan.description} onChange={(e) => setPlan({ ...plan, description: e.target.value })} disabled={submitting} />
        </div>
        <div className="form-row-pair">
          <div className="form-group">
            <label htmlFor="billingType">Billing type</label>
            <select id="billingType" className="admin-select" value={plan.billingType} onChange={(e) => setPlan({ ...plan, billingType: e.target.value as BillingType })} disabled={submitting}>
              <option value="free">free</option>
              <option value="monthly">monthly</option>
              <option value="lifetime">lifetime</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="priceCents">Price (cents)</label>
            <input id="priceCents" type="number" min={0} value={plan.priceCents} onChange={(e) => setPlan({ ...plan, priceCents: Number(e.target.value) })} disabled={submitting} />
          </div>
        </div>
        <div className="form-row-pair">
          <div className="form-group">
            <label htmlFor="providerPriceId">Provider price ID (optional)</label>
            <input id="providerPriceId" type="text" value={plan.providerPriceId ?? ''} onChange={(e) => setPlan({ ...plan, providerPriceId: e.target.value || null })} disabled={submitting} placeholder="e.g. price_1OabcStripeId" />
          </div>
          <div className="form-group">
            <label htmlFor="sortOrder">Sort order</label>
            <input id="sortOrder" type="number" value={plan.sortOrder} onChange={(e) => setPlan({ ...plan, sortOrder: Number(e.target.value) })} disabled={submitting} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-checkbox">
            <input type="checkbox" checked={plan.isActive} onChange={(e) => setPlan({ ...plan, isActive: e.target.checked })} disabled={submitting} />
            Active (shown to users)
          </label>
        </div>

        <fieldset className="form-fieldset">
          <legend>Categories unlocked by this plan</legend>
          <div className="checkbox-grid">
            {allCategories.map((c) => (
              <label key={c.id} className="form-checkbox">
                <input type="checkbox" checked={plan.categoryIds.includes(c.id)} onChange={() => toggleCategory(c.id)} disabled={submitting} />
                {c.name}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate('/admin/plans')} disabled={submitting}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </section>
  );
}
